import React, { useState } from "react";
import api from "../../lib/api";
import { DEFAULT_PROMPTS } from "./defaultPrompts";
import { CATEGORIES } from "./categories";
import TrendsModal from "./TrendsModal";
import { HiSparkles, HiArrowUpRight } from "react-icons/hi2";

export default function AIPanel({ onApply }) {
  const [prompt, setPrompt] = useState("");
  const [tone, setTone] = useState("technical");
  const [length, setLength] = useState("medium");
  const [category, setCategory] = useState("");
  const language = "en";
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [trendsOpen, setTrendsOpen] = useState(false);

  const generate = async () => {
    setError(null);
    if (prompt.trim().length < 6) {
      setError("Please write a longer prompt (or pick a default).");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/api/admin/ai/generate", {
        prompt: prompt.trim(),
        tone, length, language,
        category: category || undefined,
      });
      onApply(data.post);
    } catch (e) {
      setError(e?.response?.data?.error || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <aside className="ai-panel">
      <h3><HiSparkles /> AI assistant</h3>
      <p className="sub">
        Describe a topic — or pick a default — and I'll draft a full post in
        Markdown with title, slug, excerpt, tags and SEO metadata.
      </p>

      <div className="field">
        <label>Default prompts</label>
        <div className="prompt-chips">
          {DEFAULT_PROMPTS.map((p, i) => (
            <button key={i} type="button" onClick={() => setPrompt(p.prompt)} title={p.prompt}>
              {p.label}
            </button>
          ))}
        </div>
      </div>

      <div className="field">
        <label htmlFor="aip">Your prompt</label>
        <textarea
          id="aip"
          className="textarea"
          rows={6}
          placeholder="e.g. Practical CSP setup for a Shopify theme without breaking embedded apps…"
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
        />
      </div>

      <div className="row-3">
        <div className="field">
          <label htmlFor="cat">Category</label>
          <select id="cat" className="select" value={category} onChange={(e) => setCategory(e.target.value)}>
            <option value="">Auto</option>
            {CATEGORIES.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        </div>
        <div className="field">
          <label htmlFor="tone">Tone</label>
          <select id="tone" className="select" value={tone} onChange={(e) => setTone(e.target.value)}>
            <option value="technical">Technical</option>
            <option value="storytelling">Storytelling</option>
            <option value="tutorial">Tutorial</option>
            <option value="opinion">Opinion</option>
          </select>
        </div>
        <div className="field">
          <label htmlFor="len">Length</label>
          <select id="len" className="select" value={length} onChange={(e) => setLength(e.target.value)}>
            <option value="short">Short (~800w)</option>
            <option value="medium">Medium (~1200w)</option>
            <option value="long">Long (~2000w)</option>
          </select>
        </div>
      </div>

      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 8 }}>
        <button type="button" className="btn" onClick={generate} disabled={busy}>
          {busy ? "Generating…" : "Generate draft"}
          <span className="arrow"><HiArrowUpRight /></span>
        </button>
        <button type="button" className="btn btn--ghost" onClick={() => setTrendsOpen(true)}>
          🔥 Trending topics
        </button>
      </div>

      {error && <div className="error-msg">{error}</div>}

      <p style={{ marginTop: 16, fontSize: 12, color: "var(--muted)" }}>
        Generated content overwrites the editor fields. Saved as <strong>draft</strong> until you publish.
      </p>

      <TrendsModal
        open={trendsOpen}
        onClose={() => setTrendsOpen(false)}
        onPickTopic={(t) => setPrompt(t.prompt)}
      />
    </aside>
  );
}
